import React, { useEffect, useRef, useState } from 'react'
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, IconButton, Paper,
  Tab, Tabs, TextField, Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { readFunctionError } from '../../lib/functionError'
import Markdown from '../../components/Markdown'
import FormsWizard from '../../components/student/FormsWizard'
import { Project } from '../../types'

// Step 6 — Conduct the Experiment (sci / data science) or Develop the Solution
// (eng). Deliberately low-structure: experiments differ widely per student, so
// this collects links to the student's own Drive folders, a free-text dated log,
// and what was actually done, without imposing a shape. The mentor approves the
// preliminary observation to advance.

type Status  = 'draft' | 'submitted' | 'approved_draft' | 'completed'
type Variant = 'sci' | 'eng' | 'ds'
type CheckState = 'ok' | 'unconfirmed' | 'warn'

interface FolderCheck { state: CheckState; reason: string; checked_at: string }
interface LogEntry    { id: string; date: string; text: string }
interface Payload {
  data_folder_url?:         string
  data_folder_check?:       FolderCheck | null
  photo_folder_url?:        string
  code_url?:                string
  log?:                     LogEntry[]
  materials_actual?:        string
  procedure_actual?:        string
  preliminary_observation?: string
}

const COPY: Record<Variant, {
  title: string; data: string; dataHelp: string; photo: string; code: string; codeHelp: string
  log: string; logHelp: string; materials: string; procedure: string; observation: string; observationHelp: string
}> = {
  sci: {
    title:     'Conduct the Experiment and Record Data',
    data:      'Raw data folder',
    dataHelp:  'A Google Drive folder with your raw data — measurements, data sheets, and how many trials you ran.',
    photo:     'Setup photos folder (optional)',
    code:      'Code (optional)',
    codeHelp:  'A GitHub repository or Google Colab notebook, if you wrote code.',
    log:       'Experimental log',
    logHelp:   'A dated entry each time you work on your experiment: what you did, what you saw, anything that went differently.',
    materials: 'Materials actually used',
    procedure: 'Procedure actually followed',
    observation:     'Preliminary observation',
    observationHelp: 'What are you seeing in your data so far? Your mentor reviews this to move you to the next step.',
  },
  eng: {
    title:     'Develop the Solution',
    data:      'Test data folder',
    dataHelp:  'A Google Drive folder with your test data — measurements and results from testing your prototype.',
    photo:     'Prototype photos folder (optional)',
    code:      'Code or CAD (optional)',
    codeHelp:  'A GitHub repository, Google Colab notebook, or CAD link, if your design has one.',
    log:       'Build & test log',
    logHelp:   'A dated entry each time you work on your prototype: what you built or tested, what happened, what you changed.',
    materials: 'Materials and components actually used',
    procedure: 'Build and test process actually followed',
    observation:     'Preliminary test results',
    observationHelp: 'How is your prototype performing so far? Your mentor reviews this to move you to the next step.',
  },
  ds: {
    title:     'Conduct the Experiment and Record Data',
    data:      'Dataset',
    dataHelp:  'A Google Drive folder with your data, or a link to the dataset source you are using.',
    photo:     'Screenshots folder (optional)',
    code:      'Code (GitHub or Colab)',
    codeHelp:  'Your code is your experiment — link the GitHub repository or Google Colab notebook you ran.',
    log:       'Experiment log',
    logHelp:   'A dated entry each time you work on your experiment: what you ran, what came out, anything you changed.',
    materials: 'Data and tools actually used',
    procedure: 'Pipeline actually run',
    observation:     'Preliminary results',
    observationHelp: 'What are your results showing so far? Your mentor reviews this to move you to the next step.',
  },
}

function isUrl(s: string): boolean {
  try { return /^https?:$/.test(new URL(s.trim()).protocol) } catch { return false }
}
function isDriveLink(s: string): boolean {
  try { return new URL(s.trim()).hostname.toLowerCase() === 'drive.google.com' } catch { return false }
}
function today(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function Step6Page() {
  const navigate = useNavigate()
  const { studentId: viewerStudentId, viewerIsStudent, loading: vasLoading } = useViewerStudent()

  const [project,  setProject]  = useState<Project | null>(null)
  const [variant,  setVariant]  = useState<Variant>('sci')
  const [learnUrl, setLearnUrl] = useState<string | null>(null)
  const [status,   setStatus]   = useState<Status>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [initial,  setInitial]  = useState<Payload | null>(null)
  const [loading,  setLoading]  = useState(true)
  const [tab,      setTab]      = useState<'learn' | 'preapproval' | 'experiment' | 'summary' | 'journal'>('learn')
  const loadedStudentRef = useRef<string | null>(null)

  useEffect(() => {
    async function load() {
      if (vasLoading) return
      if (!viewerStudentId) { navigate('/'); return }
      if (loadedStudentRef.current !== viewerStudentId) setLoading(true)
      const { data: projects } = await supabase
        .from('project').select('*').eq('student_id', viewerStudentId).eq('status', 'active')
        .order('created_at', { ascending: false }).limit(1)
      const proj = projects?.[0] ?? null
      if (!proj) { setLoading(false); return }
      setProject(proj as Project)

      const [{ data: rq }, { data: hd }, { data: ed }, { data: er }, { data: st }] = await Promise.all([
        supabase.from('research_question').select('track_committed').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('hypothesis_design').select('study_mode').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('experiment_design').select('payload').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('experiment_record').select('status, payload, mentor_note').eq('project_id', proj.project_id).maybeSingle(),
        supabase.from('step').select('learn_content_url').eq('step_number', 6).maybeSingle(),
      ])
      const v: Variant = (rq as any)?.track_committed === 'engineering_design' ? 'eng'
        : (hd as any)?.study_mode === 'data_science' ? 'ds' : 'sci'
      setVariant(v)
      setLearnUrl((st as any)?.learn_content_url ?? null)

      // Seed what-actually-happened from the Step 5 plan — editable, so the
      // student records the actual by editing the plan, not retyping it.
      const plan = ((ed as any)?.payload ?? {}) as Record<string, string | undefined>
      const p = ({ ...((er as any)?.payload ?? {}) }) as Payload
      if (p.materials_actual === undefined) p.materials_actual = (plan.materials ?? '').trim()
      if (p.procedure_actual === undefined) p.procedure_actual = ((v === 'eng' ? plan.build_procedure : plan.procedure) ?? '').trim()
      setInitial(p)
      setStatus(((er as any)?.status as Status) ?? 'draft')
      setMentorNote((er as any)?.mentor_note ?? null)
      loadedStudentRef.current = viewerStudentId
      setLoading(false)
    }
    load()
  }, [viewerStudentId, vasLoading, navigate])

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  if (!project || !initial) return <Box sx={{ p: 4 }}><Typography>No active project found.</Typography></Box>

  const copy = COPY[variant]
  if ((project.current_step ?? 1) < 6) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Step 6: {copy.title}</Typography>
        <Typography color="text.secondary" sx={{ mt: 2 }}>Complete the previous steps first to unlock this one.</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>Step 6: {copy.title}</Typography>

      <Tabs value={tab} onChange={(_, t) => setTab(t)} sx={{ mb: 3 }} variant="scrollable" allowScrollButtonsMobile>
        <Tab label="Learn" value="learn" />
        <Tab label="Pre-Approval" value="preapproval" />
        <Tab label="Experiment" value="experiment" />
        <Tab label="Summary" value="summary" />
        <Tab label="Journal" value="journal" />
      </Tabs>

      {tab === 'learn' && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          {learnUrl ? (
            <>
              <Typography sx={{ mb: 2 }}>Review the lesson for this step before you start.</Typography>
              <Button variant="contained" href={learnUrl} target="_blank" rel="noopener noreferrer">Open lesson</Button>
            </>
          ) : (
            <Typography color="text.secondary">
              Lesson content for this step will be added by your mentor. You can go to the Experiment tab to get started.
            </Typography>
          )}
        </Paper>
      )}

      {tab === 'preapproval' && (
        <FormsWizard projectId={project.project_id} studentId={project.student_id} editable={viewerIsStudent} />
      )}

      <Workspace
        key={project.project_id}
        tab={tab}
        projectId={project.project_id}
        studentId={project.student_id}
        variant={variant}
        initial={initial}
        status={status}
        mentorNote={mentorNote}
        editable={viewerIsStudent}
        onStatus={(s) => { setStatus(s); if (s === 'submitted') setMentorNote(null) }}
      />
    </Box>
  )
}

// Experiment, Summary and Journal share one payload, so they live together:
// switching tabs must never drop unsaved input.
function Workspace({ tab, projectId, studentId, variant, initial, status, mentorNote, editable, onStatus }: {
  tab:        string
  projectId:  string
  studentId:  string
  variant:    Variant
  initial:    Payload
  status:     Status
  mentorNote: string | null
  editable:   boolean
  onStatus:   (s: Status) => void
}) {
  const copy = COPY[variant]
  const [dataUrl,     setDataUrl]     = useState(initial.data_folder_url ?? '')
  const [dataCheck,   setDataCheck]   = useState<FolderCheck | null>(initial.data_folder_check ?? null)
  const [photoUrl,    setPhotoUrl]    = useState(initial.photo_folder_url ?? '')
  const [codeUrl,     setCodeUrl]     = useState(initial.code_url ?? '')
  const [log,         setLog]         = useState<LogEntry[]>(initial.log ?? [])
  const [materials,   setMaterials]   = useState(initial.materials_actual ?? '')
  const [procedure,   setProcedure]   = useState(initial.procedure_actual ?? '')
  const [observation, setObservation] = useState(initial.preliminary_observation ?? '')
  const [checking,    setChecking]    = useState(false)
  const [submitting,  setSubmitting]  = useState(false)
  const [error,       setError]       = useState<string | null>(null)
  const [saveState,   setSaveState]   = useState<'idle' | 'saving' | 'saved'>('idle')

  const isReadOnly = !editable || status === 'submitted' || status === 'approved_draft' || status === 'completed'
  const isApproved = status === 'approved_draft' || status === 'completed'

  // A non-Drive dataset link (data science only) can't be checked — a valid URL is enough.
  const dataOk = variant === 'ds' && dataUrl.trim() && !isDriveLink(dataUrl)
    ? isUrl(dataUrl)
    : !!dataCheck && dataCheck.state !== 'warn'
  const locked = !isReadOnly && !dataOk
  const codeOk = variant !== 'ds' || isUrl(codeUrl)
  const linksOk = (!photoUrl.trim() || isUrl(photoUrl)) && (!codeUrl.trim() || isUrl(codeUrl))
  const readyToSubmit = dataOk && codeOk && linksOk && !!observation.trim()

  function payload(): Payload {
    return {
      data_folder_url: dataUrl.trim(), data_folder_check: dataCheck,
      photo_folder_url: photoUrl.trim(), code_url: codeUrl.trim(),
      log, materials_actual: materials, procedure_actual: procedure,
      preliminary_observation: observation,
    }
  }

  async function save() {
    if (isReadOnly) return
    setSaveState('saving')
    const { error: upErr } = await supabase.from('experiment_record').upsert({
      project_id: projectId, student_id: studentId,
      payload: payload(), updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id' })
    setSaveState(upErr ? 'idle' : 'saved')
    if (upErr) setError(`Could not save: ${upErr.message}`)
  }

  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return }
    if (isReadOnly) return
    const t = setTimeout(() => { save() }, 900)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUrl, JSON.stringify(dataCheck), photoUrl, codeUrl, JSON.stringify(log), materials, procedure, observation])

  async function checkFolder() {
    setChecking(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step6-check-folder', { body: { url: dataUrl.trim() } })
      if (invokeErr) throw new Error(await readFunctionError(invokeErr))
      setDataCheck(data as FolderCheck)
    } catch (e: any) {
      setError(e.message ?? 'Could not check the link — please try again.')
    } finally {
      setChecking(false)
    }
  }

  async function submit() {
    if (submitting || !readyToSubmit) return
    setSubmitting(true); setError(null)
    const { error: upErr } = await supabase.from('experiment_record').upsert({
      project_id: projectId, student_id: studentId, payload: payload(),
      status: 'submitted', mentor_note: null, updated_at: new Date().toISOString(),
    }, { onConflict: 'project_id' })
    setSubmitting(false)
    if (upErr) { setError(`Could not submit: ${upErr.message}`); return }
    onStatus('submitted')
  }

  const banner = (
    <>
      {isApproved && <Alert severity="success" sx={{ mb: 2 }}>Approved by your mentor — Step 7 is unlocked.</Alert>}
      {status === 'submitted' && <Alert severity="info" sx={{ mb: 2 }}>Submitted — waiting for your mentor's review.</Alert>}
      {status === 'draft' && mentorNote && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Your mentor sent this back</Typography>
          <Markdown>{mentorNote}</Markdown>
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!isReadOnly && saveState !== 'idle' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 1 }}>
          {saveState === 'saving' ? 'Saving…' : 'Saved'}
        </Typography>
      )}
    </>
  )

  if (tab === 'experiment') {
    const dataNeedsCheck = !(variant === 'ds' && dataUrl.trim() && !isDriveLink(dataUrl))
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {banner}
        <Card variant="outlined"><CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{copy.data}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {copy.dataHelp} In Drive, click Share and set General access to "Anyone with the link" so your mentor can open it.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              fullWidth size="small" placeholder="https://drive.google.com/drive/folders/…"
              value={dataUrl}
              onChange={e => { setDataUrl(e.target.value); setDataCheck(null) }}
              disabled={isReadOnly}
            />
            {dataNeedsCheck && !isReadOnly && (
              <Button variant="contained" onClick={checkFolder} disabled={checking || !dataUrl.trim()} sx={{ whiteSpace: 'nowrap', height: 40 }}>
                {checking ? <CircularProgress size={18} color="inherit" /> : 'Check link'}
              </Button>
            )}
          </Box>
          {dataNeedsCheck && dataCheck && (
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 1 }}>
              {dataCheck.state === 'ok'
                ? <CheckCircleIcon fontSize="small" color="success" />
                : <WarningAmberIcon fontSize="small" color={dataCheck.state === 'warn' ? 'error' : 'warning'} />}
              <Typography variant="body2" color={dataCheck.state === 'warn' ? 'error' : 'text.secondary'}>{dataCheck.reason}</Typography>
            </Box>
          )}
          {locked && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {dataNeedsCheck ? 'Check your folder link to unlock the rest of this step.' : 'Enter a full https:// link to unlock the rest of this step.'}
            </Typography>
          )}
        </CardContent></Card>

        <LinkField label={copy.photo} help="A Google Drive folder of photos — keep them in Drive, not here." value={photoUrl} onChange={setPhotoUrl} disabled={isReadOnly || locked} />
        <LinkField label={copy.code} help={copy.codeHelp} value={codeUrl} onChange={setCodeUrl} disabled={isReadOnly || locked} />

        <Card variant="outlined"><CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{copy.log}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{copy.logHelp}</Typography>
          {log.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>No entries yet.</Typography>
          )}
          {log.map((entry, i) => (
            <Box key={entry.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
              <TextField
                type="date" size="small" sx={{ width: 170, flexShrink: 0 }}
                value={entry.date}
                onChange={e => setLog(l => l.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))}
                disabled={isReadOnly || locked}
              />
              <TextField
                fullWidth multiline minRows={2} size="small"
                value={entry.text}
                onChange={e => setLog(l => l.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                disabled={isReadOnly || locked}
              />
              {!isReadOnly && (
                <IconButton aria-label="Remove entry" onClick={() => setLog(l => l.filter((_, j) => j !== i))} disabled={locked}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          {!isReadOnly && (
            <Button
              variant="outlined" size="small" disabled={locked}
              onClick={() => setLog(l => [...l, { id: crypto.randomUUID(), date: today(), text: '' }])}
            >
              Add entry
            </Button>
          )}
        </CardContent></Card>
      </Box>
    )
  }

  if (tab === 'summary') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {banner}
        <Typography variant="body2" color="text.secondary">
          Record what you actually did. Your Step 5 plan is filled in to start from — change it to match what really happened.
        </Typography>
        <TextField label={copy.materials} multiline minRows={4} fullWidth value={materials} onChange={e => setMaterials(e.target.value)} disabled={isReadOnly} />
        <TextField label={copy.procedure} multiline minRows={6} fullWidth value={procedure} onChange={e => setProcedure(e.target.value)} disabled={isReadOnly} />
        <TextField
          label={copy.observation} helperText={copy.observationHelp}
          multiline minRows={5} fullWidth value={observation} onChange={e => setObservation(e.target.value)} disabled={isReadOnly}
        />
        {!isReadOnly && (
          <Box>
            <Button variant="contained" onClick={submit} disabled={!readyToSubmit || submitting}>
              {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit to your mentor'}
            </Button>
            {!readyToSubmit && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {!dataOk ? `Add and check your ${copy.data.toLowerCase()} on the Experiment tab first.`
                  : !codeOk ? 'Add your code link on the Experiment tab first.'
                  : !linksOk ? 'Fix the links on the Experiment tab — each must be a full https:// link.'
                  : `Write your ${copy.observation.toLowerCase()} to submit.`}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    )
  }

  if (tab === 'journal') {
    const entries = [...log].sort((a, b) => a.date.localeCompare(b.date))
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {mentorNote && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">Mentor's note</Typography>
            <Markdown>{mentorNote}</Markdown>
          </Paper>
        )}
        {entries.length === 0 && !mentorNote && (
          <Paper variant="outlined" sx={{ p: 3 }}><Typography color="text.secondary">No journal entries yet.</Typography></Paper>
        )}
        {entries.map(e => (
          <Paper key={e.id} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">{e.date} · {copy.log}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{e.text}</Typography>
          </Paper>
        ))}
      </Box>
    )
  }

  return null
}

function LinkField({ label, help, value, onChange, disabled }: {
  label: string; help: string; value: string; onChange: (v: string) => void; disabled: boolean
}) {
  const bad = !!value.trim() && !isUrl(value)
  return (
    <Card variant="outlined"><CardContent>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{help}</Typography>
      <TextField
        fullWidth size="small" placeholder="https://…" value={value}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        error={bad} helperText={bad ? 'Paste a full https:// link.' : undefined}
      />
      {!disabled && value.trim() && isUrl(value) && (
        <Button size="small" href={value.trim()} target="_blank" rel="noopener noreferrer" sx={{ mt: 0.5, px: 0 }}>Open link</Button>
      )}
    </CardContent></Card>
  )
}
